import React, { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Plus, Pencil, Trash2, AlertTriangle, Bot } from "lucide-react";
import {
  PageHeader, Button, Input, Textarea, Select, Modal, Card, CardContent, StatusBadge,
  LoadingBlock, EmptyState, ConfirmDialog, toast,
} from "@/components/ds";
import { api, formatApiError, type MembershipConfig, type MembershipPlan, type MembershipEngagement, type MembershipMetrics } from "@/lib/api";

const STAGES = ["offered", "interested", "follow_up_requested", "enrolled"] as const;
const STAGE_LABEL: Record<string, string> = {
  offered: "Offered", interested: "Interested", follow_up_requested: "Follow-up requested", enrolled: "Enrolled",
};
const STAGE_TONE: Record<string, "slate" | "blue" | "amber" | "green"> = {
  offered: "slate", interested: "blue", follow_up_requested: "amber", enrolled: "green",
};
const INTERVALS = [
  { value: "monthly", label: "per month" }, { value: "quarterly", label: "per quarter" },
  { value: "yearly", label: "per year" }, { value: "one_time", label: "one-time" },
];

const EMPTY = { name: "", description: "", price: "", price_interval: "monthly", benefits: "", eligibility: "", talking_points: "", active: true };

export default function Memberships() {
  const [config, setConfig] = useState<MembershipConfig | null>(null);
  const [plans, setPlans] = useState<MembershipPlan[] | null>(null);
  const [engagements, setEngagements] = useState<MembershipEngagement[] | null>(null);
  const [metrics, setMetrics] = useState<MembershipMetrics | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<MembershipPlan | null>(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<MembershipPlan | null>(null);

  const load = useCallback(() => {
    api.membershipPlans().then(setPlans).catch((e) => toast.error(formatApiError(e)));
    api.membershipEngagements().then(setEngagements).catch(() => {});
    api.membershipMetrics().then(setMetrics).catch(() => {});
  }, []);
  useEffect(() => { api.membershipConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { if (config?.included) load(); else { setPlans([]); setEngagements([]); } }, [config, load]);

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY }); setShowForm(true); };
  const openEdit = (p: MembershipPlan) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description ?? "", price: p.price != null ? String(p.price) : "",
      price_interval: p.price_interval || "monthly", benefits: (p.benefits || []).join("\n"),
      eligibility: p.eligibility ?? "", talking_points: (p.talking_points || []).join("\n"), active: p.active,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the membership a name"); return; }
    setBusy(true);
    const body = {
      name: form.name.trim(), description: form.description.trim() || null,
      price: form.price === "" ? null : Number(form.price), price_interval: form.price_interval,
      benefits: form.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
      eligibility: form.eligibility.trim() || null,
      talking_points: form.talking_points.split("\n").map((s) => s.trim()).filter(Boolean),
      active: form.active,
    };
    try {
      if (editing) await api.membershipPlanUpdate(editing.id, body);
      else await api.membershipPlanCreate(body);
      toast.success(editing ? "Membership updated" : "Membership created");
      setShowForm(false); load(); api.membershipConfig().then(setConfig).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await api.membershipPlanDelete(confirmDelete.id); toast.success("Membership deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setConfirmDelete(null); }
  };

  const changeStage = async (e: MembershipEngagement, stage: string) => {
    try { await api.membershipStageChange(e.id, stage); toast.success("Stage updated"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const funnel = metrics?.funnel || {};

  return (
    <div className="space-y-6" data-testid="page-memberships">
      <PageHeader title="Maintenance Memberships" description="Define your recurring membership plans — your AI mentions them appropriately and tracks interest through to enrollment." testid="memberships-header"
        action={config?.included ? <Button onClick={openCreate} data-testid="membership-new-btn"><Plus className="h-4 w-4" /> New membership</Button> : undefined} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="memberships-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.included
            ? <StatusBadge tone="green">Included · up to {config.max_plans} plans</StatusBadge>
            : <StatusBadge tone="amber">Not included — upgrade to Growth+</StatusBadge>}
          {config.included && config.analytics && <StatusBadge tone="green">Advanced analytics</StatusBadge>}
        </div>
      )}

      {config && config.included && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="memberships-honest-note">
          <Bot className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <p className="text-xs text-blue-800">Your AI receptionist will only mention the <b>approved talking points</b> you enter below — it never invents membership terms, pricing or benefits. Enrollment here is a <b>status record</b> inside ResFil; connecting real recurring billing is a separate integration (currently <b>not connected</b>).</p>
        </div>
      )}

      {config && !config.included && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700" data-testid="memberships-upgrade">
          Membership promotion is available on Growth and above. Upgrade to let your AI promote your maintenance memberships and track interest through to enrollment.
        </div>
      )}

      {config?.included && (
        <>
          {/* Funnel */}
          <div className="grid gap-4 sm:grid-cols-4" data-testid="memberships-funnel">
            {STAGES.map((s) => (
              <div key={s} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-slate-500">{STAGE_LABEL[s]}</div>
                <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{funnel[s] ?? 0}</div>
              </div>
            ))}
          </div>
          {metrics && metrics.total_engaged > 0 && (
            <p className="text-xs text-slate-500" data-testid="memberships-conv-rate">Enrollment conversion: <b>{metrics.enroll_conversion_rate}%</b> of {metrics.total_engaged} engaged customers (last 30 days).</p>
          )}

          {/* Plans */}
          <div>
            <h2 className="mb-3 text-sm font-semibold text-slate-800">Your membership plans</h2>
            {plans === null ? <LoadingBlock rows={3} /> : plans.length === 0 ? (
              <EmptyState icon={BadgeCheck} title="No membership plans yet" description="Create a plan with approved AI talking points so your receptionist can promote it." />
            ) : (
              <div className="grid gap-4 md:grid-cols-2" data-testid="memberships-plans">
                {plans.map((p) => (
                  <Card key={p.id}><CardContent className="space-y-3 p-4" data-testid={`membership-plan-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-display text-lg font-semibold text-slate-900">{p.name}</h3>
                          {p.active ? <StatusBadge tone="green">Active</StatusBadge> : <StatusBadge tone="slate">Inactive</StatusBadge>}
                        </div>
                        <p className="text-sm text-slate-500">{p.price != null ? `$${p.price} ${INTERVALS.find((i) => i.value === p.price_interval)?.label || p.price_interval}` : "Custom pricing"}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`membership-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(p)} data-testid={`membership-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    {p.description && <p className="text-sm text-slate-600">{p.description}</p>}
                    {p.benefits.length > 0 && (
                      <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-600">
                        {p.benefits.map((b, i) => <li key={i}>{b}</li>)}
                      </ul>
                    )}
                    {p.eligibility && <p className="text-xs text-slate-500"><b>Eligibility:</b> {p.eligibility}</p>}
                    {p.talking_points.length > 0 && (
                      <div className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                        <span className="font-medium text-slate-700">AI talking points:</span>
                        <ul className="mt-1 list-disc space-y-0.5 pl-4">{p.talking_points.map((t, i) => <li key={i}>{t}</li>)}</ul>
                      </div>
                    )}
                    <StatusBadge tone="slate"><AlertTriangle className="mr-1 inline h-3 w-3" /> Billing: not connected</StatusBadge>
                  </CardContent></Card>
                ))}
              </div>
            )}
          </div>

          {/* Engagements */}
          <div>
            <h2 className="mb-3 text-sm font-semibold text-slate-800">Membership engagement</h2>
            {engagements === null ? <LoadingBlock rows={3} /> : engagements.length === 0 ? (
              <EmptyState icon={BadgeCheck} title="No engagement yet" description="When your AI offers a membership or a customer shows interest, it appears here." />
            ) : (
              <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="memberships-engagements">
                {engagements.map((e) => (
                  <div key={e.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`membership-engagement-${e.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                        {e.contact_name || "Customer"} <span className="text-slate-400">·</span> <span className="text-slate-500">{e.membership_plan_name}</span>
                      </div>
                      <p className="text-xs text-slate-400">via {e.last_source} · updated {new Date(e.updated_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge tone={STAGE_TONE[e.stage] || "slate"}>{STAGE_LABEL[e.stage] || e.stage}</StatusBadge>
                      <div className="w-44">
                        <Select value={e.stage} onChange={(v) => changeStage(e, v)}
                          options={STAGES.map((s) => ({ value: s, label: STAGE_LABEL[s] }))}
                          testid={`membership-stage-select-${e.id}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent></Card>
            )}
          </div>
        </>
      )}

      <Modal open={showForm} onOpenChange={setShowForm} title={editing ? "Edit membership" : "New membership"} testid="membership-form-modal">
        <div className="space-y-3">
          <div><p className="mb-1 text-xs font-medium text-slate-500">Plan name</p>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Comfort Club Maintenance Plan" data-testid="membership-name" /></div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Description</p>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="membership-description" /></div>
          <div className="flex gap-3">
            <div className="w-32"><p className="mb-1 text-xs font-medium text-slate-500">Price ($)</p>
              <Input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="e.g. 19" data-testid="membership-price" /></div>
            <div className="flex-1"><p className="mb-1 text-xs font-medium text-slate-500">Billing interval</p>
              <Select value={form.price_interval} onChange={(v) => setForm({ ...form, price_interval: v })} options={INTERVALS} data-testid="membership-interval" /></div>
          </div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Benefits <span className="text-slate-400">(one per line)</span></p>
            <Textarea value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} rows={3} placeholder={"2 tune-ups per year\nPriority scheduling\n15% off repairs"} data-testid="membership-benefits" /></div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Eligibility</p>
            <Textarea value={form.eligibility} onChange={(e) => setForm({ ...form, eligibility: e.target.value })} rows={2} placeholder="e.g. Homeowners within our service area" data-testid="membership-eligibility" /></div>
          <div><p className="mb-1 text-xs font-medium text-slate-500">Approved AI talking points <span className="text-slate-400">(one per line — the AI may only say these)</span></p>
            <Textarea value={form.talking_points} onChange={(e) => setForm({ ...form, talking_points: e.target.value })} rows={3} placeholder={"Our members never pay overtime fees\nMembers get same-day priority booking"} data-testid="membership-talking-points" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="membership-active" /> Active (AI may offer this)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowForm(false)} data-testid="membership-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="membership-save-btn">{editing ? "Save changes" : "Create membership"}</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }} onConfirm={doDelete}
        title="Deactivate membership?" description="Your AI will stop offering this plan. Engagement history is preserved."
        confirmLabel="Deactivate" destructive testid="membership-confirm-delete" />
    </div>
  );
}
